import { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { AdminAuditLogDTO, AdminDTO, AdminPermissionDTO } from "@/gen/api"
import CouncilMultiSelect from "@/components/council-multi-select"
import { DeleteAdminModal } from "@/components/DeleteAdminModal"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { CreatedAtDisplay, LastLoginDisplay } from "@/components/ui/date-display"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/useAuth"
import { adminManagementApi, councilAdminApi } from "@/lib/api"
import {
  AdminPermissionSelection,
  AdminRolePreset,
  emptyPermissionSelection,
  getPermissionSelection,
  hasPermission,
  mergePermissionSelection,
  mergePermissionSelectionForCouncils,
  permissionSelectionForRoles,
} from "@/lib/admin-permissions"
import AdminPermissionMatrix from "@/components/AdminPermissionMatrix"
import { queryClient } from "@/lib/queryClient"

const LANGUAGE_OPTIONS = [
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-US", label: "English (United States)" },
  { value: "en-GB", label: "English (United Kingdom)" },
  { value: "vi", label: "Vietnamese" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
]

const adminName = (admin?: AdminDTO | null) =>
  admin?.name ||
  [admin?.firstName, admin?.lastName].filter(Boolean).join(" ") ||
  admin?.email ||
  "Admin"

export default function AdminDetailsPage() {
  const { councilId, adminId } = useParams()
  const scopedCouncilId = councilId ? Number(councilId) : undefined
  const navigate = useNavigate()
  const location = useLocation()
  const isCouncilAdminsSurface = location.pathname.startsWith("/manage-admin/council-admins")
  const { toast } = useToast()
  const { user } = useAuth()
  const [admin, setAdmin] = useState<AdminDTO | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    enforceTwoFactor: false,
    resetTwoFactor: false,
    language: "en-AU",
    enableAlarmEmails: false,
    status: "active",
    role: "admin",
    rolePresets: ["admin"] as AdminRolePreset[],
    councilIds: [] as number[],
    permissions: [] as AdminPermissionDTO[],
    permissionSelection: emptyPermissionSelection() as AdminPermissionSelection,
  })
  const [auditLogs, setAuditLogs] = useState<AdminAuditLogDTO[]>([])
  const [isAuditLoading, setIsAuditLoading] = useState(false)

  const listPath = scopedCouncilId
    ? `/council/${scopedCouncilId}/manage-admin`
    : isCouncilAdminsSurface
      ? "/manage-admin/council-admins"
      : "/manage-admin"
  const adminNumericId = adminId ? Number(adminId) : NaN

  useEffect(() => {
    if (!Number.isFinite(adminNumericId)) {
      navigate(listPath, { replace: true })
      return
    }

    const loadAdmin = async () => {
      setIsLoading(true)
      try {
        const response = scopedCouncilId
          ? await councilAdminApi.getCouncilAdmin(scopedCouncilId, adminNumericId)
          : await adminManagementApi.getAdmin(String(adminNumericId))
        const detail = response.data.data
        if (!detail) throw new Error("Admin not found")
        setAdmin(detail)
        setForm({
          firstName: detail.firstName ?? "",
          lastName: detail.lastName ?? "",
          email: detail.email ?? "",
          phoneNumber: detail.phoneNumber ?? "",
          password: "",
          enforceTwoFactor: Boolean(detail.enforceTwoFactor),
          resetTwoFactor: false,
          language: detail.language ?? "en-AU",
          enableAlarmEmails: Boolean(detail.enableAlarmEmails),
          status: detail.status ?? "active",
          role: normalizeRole(detail.role),
          rolePresets: rolePresetsFromDetail(
            detail.role,
            detail.permissions ?? [],
            scopedCouncilId,
          ),
          councilIds: detail.councils?.map((council) => Number(council.id)).filter(Boolean) ?? [],
          permissions: detail.permissions ?? [],
          permissionSelection: getPermissionSelection(detail.permissions ?? [], scopedCouncilId),
        })
      } catch (error) {
        toast({
          title: "Error",
          description: (error as Error).message || "Failed to load admin details",
          variant: "destructive",
        })
        navigate(listPath, { replace: true })
      } finally {
        setIsLoading(false)
      }
    }

    void loadAdmin()
  }, [adminNumericId, listPath, navigate, scopedCouncilId, toast])

  useEffect(() => {
    if (!Number.isFinite(adminNumericId)) return
    const loadAuditLogs = async () => {
      setIsAuditLoading(true)
      try {
        const response = scopedCouncilId
          ? await councilAdminApi.getCouncilAdminAuditLogs(scopedCouncilId, adminNumericId)
          : await adminManagementApi.getAdminAuditLogs(String(adminNumericId))
        setAuditLogs(response.data.data ?? [])
      } catch (error) {
        toast({
          title: "Error",
          description: (error as Error).message || "Failed to load audit log",
          variant: "destructive",
        })
      } finally {
        setIsAuditLoading(false)
      }
    }
    void loadAuditLogs()
  }, [adminNumericId, scopedCouncilId, toast])

  const isSelf = admin?.id != null && user?.id != null && Number(admin.id) === Number(user.id)
  const actionIsRevoke = Boolean(scopedCouncilId && (admin?.councils?.length ?? 0) > 1)
  const canWriteAllAdmin =
    Boolean(user?.role === "super_admin") ||
    hasPermission(user?.permissions, "admin_management", "write_all", scopedCouncilId)
  const canWriteOwnAdmin = hasPermission(
    user?.permissions,
    "admin_management",
    "write_own",
    scopedCouncilId,
  )
  const canWriteAdmin =
    Boolean(user?.role === "super_admin") || canWriteAllAdmin || Boolean(isSelf && canWriteOwnAdmin)
  const canEditDetails = canWriteAdmin
  const canEditGlobalAccess = Boolean(
    user?.role === "super_admin" && !scopedCouncilId && form.role !== "super_admin",
  )
  const canEditRole = Boolean(
    user?.role === "super_admin" && !scopedCouncilId && !isCouncilAdminsSurface,
  )
  const detailsCopy = isCouncilAdminsSurface
    ? {
        backLabel: "Back to Council Admins",
        profileDescription:
          "Contact and identity details for this council admin across Water Bureaus.",
        headingHelp:
          "Manage this admin's global council access, role preset, permissions, and account controls.",
        permissionsDescription:
          "Choose the baseline permissions this council admin receives in their assigned council scopes.",
        accessTitle: "Global Council Access",
        accessDescription:
          "Assign the councils this admin can access. Existing explicit grants stay attached unless a council is removed.",
        councilAccessLabel: "Assigned Councils",
        councilAccessHelp:
          "These assignments control the councils where this admin can sign in and inherit council-scoped permissions.",
      }
    : {
        backLabel: "Back to Manage Admins",
        profileDescription: "Contact and identity details for this admin.",
        headingHelp: "Manage this admin's profile, permissions, access, and account controls.",
        permissionsDescription:
          "Choose exactly what this admin can do in this admin's council scope.",
        accessTitle: "Role & Council Access",
        accessDescription:
          "Only unrestricted super admins can change N3 admin council assignments.",
        councilAccessLabel: "Council Access",
        councilAccessHelp: "Select the councils this admin can access.",
      }

  const saveDetails = async () => {
    if (!admin?.id) return
    if (!canEditDetails) return
    setIsSaving(true)
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        password: form.password || undefined,
        language: form.language,
        enableAlarmEmails: form.enableAlarmEmails,
        status: form.status,
        enforceTwoFactor: form.enforceTwoFactor,
        resetTwoFactor: form.resetTwoFactor,
        role: isCouncilAdminsSurface
          ? "council_admin"
          : form.rolePresets.includes("super_admin")
            ? "super_admin"
            : "admin",
        councilIds: canEditGlobalAccess ? form.councilIds : undefined,
        permissions: scopedCouncilId
          ? mergePermissionSelectionForCouncils(form.permissions, form.permissionSelection, [
              scopedCouncilId,
            ])
          : mergePermissionSelection(form.permissions, form.permissionSelection, scopedCouncilId),
      }

      if (scopedCouncilId) {
        await councilAdminApi.updateCouncilAdmin(scopedCouncilId, Number(admin.id), payload)
      } else {
        await adminManagementApi.updateAdmin(String(admin.id), payload)
        await queryClient.invalidateQueries({ queryKey: ["admins"] })
      }

      toast({ title: "Admin Updated", description: `${adminName(admin)} has been updated.` })
      navigate(listPath)
    } catch (error) {
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to update admin",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const deleteAdmin = async () => {
    if (!admin?.id) return
    setIsDeleting(true)
    try {
      if (scopedCouncilId) {
        await councilAdminApi.deleteCouncilAdmin(scopedCouncilId, Number(admin.id))
      } else {
        await adminManagementApi.deleteAdmin(String(admin.id))
        await queryClient.invalidateQueries({ queryKey: ["admins"] })
      }
      toast({
        title: actionIsRevoke ? "Admin Access Revoked" : "Admin Deleted",
        description: actionIsRevoke
          ? `${adminName(admin)} has been removed from this council.`
          : `${adminName(admin)} has been deleted.`,
      })
      navigate(listPath)
    } catch (error) {
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to delete admin",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
      setDeleteOpen(false)
    }
  }

  if (isLoading) {
    return <div className="py-12 text-center text-muted-foreground">Loading admin details...</div>
  }

  if (!admin) return null

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-3">
          <Button variant="ghost" className="w-fit gap-2 px-0" onClick={() => navigate(listPath)}>
            <ArrowLeft className="h-4 w-4" /> {detailsCopy.backLabel}
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{adminName(admin)}</h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              {detailsCopy.headingHelp}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="secondary">{roleBadgeLabel(admin.role)}</Badge>
              <Badge variant={admin.status === "active" ? "default" : "outline"}>
                {capitalizeLabel(admin.status ?? "unknown")}
              </Badge>
              {admin.enforceTwoFactor && <Badge variant="outline">2FA enforced</Badge>}
              {admin.twoFactorEnabled && <Badge variant="secondary">2FA enabled</Badge>}
            </div>
          </div>
        </div>
        <div className="grid gap-2 rounded-lg border bg-muted/30 p-4 text-sm md:min-w-72">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Last login</span>
            <LastLoginDisplay date={admin.lastLogin} className="font-medium" />
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Created</span>
            <CreatedAtDisplay date={admin.createdAt} className="font-medium" />
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>{detailsCopy.profileDescription}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="admin-first-name">First Name</Label>
                  <Input
                    id="admin-first-name"
                    value={form.firstName}
                    disabled={!canEditDetails}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, firstName: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-last-name">Last Name</Label>
                  <Input
                    id="admin-last-name"
                    value={form.lastName}
                    disabled={!canEditDetails}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, lastName: event.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="admin-detail-email">Email</Label>
                  <Input
                    id="admin-detail-email"
                    type="email"
                    value={form.email}
                    disabled={!canEditDetails}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, email: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-phone">Phone Number</Label>
                  <Input
                    id="admin-phone"
                    value={form.phoneNumber}
                    disabled={!canEditDetails}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, phoneNumber: event.target.value }))
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Permissions</CardTitle>
              <CardDescription>{detailsCopy.permissionsDescription}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <AdminPermissionMatrix
                idPrefix="admin-detail-permission"
                value={form.permissionSelection}
                disabled={!canEditDetails}
                onChange={(permissionSelection) => {
                  if (canEditDetails) setForm((prev) => ({ ...prev, permissionSelection }))
                }}
              />
              {!canEditDetails && (
                <p className="text-xs text-muted-foreground">
                  Admin Management write permission is required to change permissions.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Other existing permissions are preserved when these checkboxes change.
              </p>
            </CardContent>
          </Card>

          {!scopedCouncilId && (
            <Card>
              <CardHeader>
                <CardTitle>{detailsCopy.accessTitle}</CardTitle>
                <CardDescription>{detailsCopy.accessDescription}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Roles</div>
                  <div className="grid gap-2 rounded-md border p-3">
                    {(["super_admin", "admin", "read_only"] as AdminRolePreset[]).map((role) => (
                      <div key={role} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={form.rolePresets.includes(role)}
                          disabled={!canEditRole}
                          onCheckedChange={(checked) => {
                            const nextRoles = checked
                              ? Array.from(new Set([...form.rolePresets, role]))
                              : form.rolePresets.filter((value) => value !== role)
                            const safeRoles: AdminRolePreset[] = nextRoles.length
                              ? nextRoles
                              : ["read_only"]
                            setForm((prev) => ({
                              ...prev,
                              rolePresets: safeRoles,
                              role: safeRoles.includes("super_admin") ? "super_admin" : "admin",
                              councilIds: safeRoles.includes("super_admin") ? [] : prev.councilIds,
                              permissionSelection: permissionSelectionForRoles(safeRoles),
                            }))
                          }}
                        />
                        <span>
                          {role === "super_admin"
                            ? "N3 Admin"
                            : role === "admin"
                              ? "Admin"
                              : "Read Only"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {form.role !== "super_admin" && (
                  <div className="space-y-2">
                    <Label>{detailsCopy.councilAccessLabel}</Label>
                    <CouncilMultiSelect
                      value={form.councilIds}
                      onChange={(councilIds) => setForm((prev) => ({ ...prev, councilIds }))}
                      disabled={!canEditGlobalAccess}
                    />
                    <p className="text-sm text-muted-foreground">{detailsCopy.councilAccessHelp}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Security</CardTitle>
              <CardDescription>Password, suspension, and two-factor controls.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-password-reset">New Password</Label>
                <Input
                  id="admin-password-reset"
                  type="password"
                  placeholder="Leave blank to keep existing password"
                  value={form.password}
                  disabled={!canEditDetails}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div>
                  <Label>Suspended</Label>
                  <p className="text-sm text-muted-foreground">
                    Disable this admin's account without deleting it.
                  </p>
                </div>
                <Switch
                  checked={form.status !== "active"}
                  disabled={!canEditDetails}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, status: checked ? "inactive" : "active" }))
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div>
                  <Label>Enforce 2FA</Label>
                  <p className="text-sm text-muted-foreground">
                    Require this admin to set up and use two-factor authentication.
                  </p>
                </div>
                <Switch
                  checked={form.enforceTwoFactor}
                  disabled={!canEditDetails}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, enforceTwoFactor: checked }))
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div>
                  <Label>Reset 2FA Setup</Label>
                  <p className="text-sm text-muted-foreground">
                    Force a fresh authenticator setup on next login.
                  </p>
                </div>
                <Switch
                  checked={form.resetTwoFactor}
                  disabled={!canEditDetails}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, resetTwoFactor: checked }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preferences</CardTitle>
              <CardDescription>Timezone uses the admin's local browser timezone.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-language">Language</Label>
                <Select
                  value={form.language}
                  disabled={!canEditDetails}
                  onValueChange={(language) => setForm((prev) => ({ ...prev, language }))}
                >
                  <SelectTrigger id="admin-language">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div>
                  <Label>Alarm Emails</Label>
                  <p className="text-sm text-muted-foreground">
                    Send leak and alarm notifications by email.
                  </p>
                </div>
                <Switch
                  checked={form.enableAlarmEmails}
                  disabled={!canEditDetails}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, enableAlarmEmails: checked }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>
                {scopedCouncilId
                  ? "Delete this admin, or revoke access from the current council when other council assignments remain."
                  : "Delete this N3 admin account."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                disabled={isSelf || isDeleting || !canWriteAdmin}
                onClick={() => setDeleteOpen(true)}
              >
                {actionIsRevoke ? "Revoke Council Access" : "Delete Admin"}
              </Button>
              {isSelf && (
                <p className="mt-2 text-sm text-muted-foreground">
                  You cannot delete your own admin account.
                </p>
              )}
              {!canWriteAdmin && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Admin Management write permission is required.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
          <CardDescription>
            Actions this admin performed or that were recorded for this admin across N3 and
            councils.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isAuditLoading ? (
            <p className="text-sm text-muted-foreground">Loading audit log...</p>
          ) : auditLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No audit log entries yet.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {auditLogs.map((log) => (
                <div
                  key={log.id}
                  className="grid gap-2 p-4 md:grid-cols-[180px_minmax(0,1fr)_220px]"
                >
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(log.occurredAt)}
                  </div>
                  <div>
                    <p className="font-medium">{log.action}</p>
                    <p className="text-sm text-muted-foreground">
                      {[log.location, log.targetName, log.metadata].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="text-sm text-muted-foreground md:text-right">
                    {log.actorEmail ? `By ${log.actorEmail}` : "System"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-background/95 py-4 backdrop-blur">
        <Button variant="outline" onClick={() => navigate(listPath)}>
          Cancel
        </Button>
        <Button onClick={saveDetails} disabled={isSaving || !canEditDetails}>
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <DeleteAdminModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        adminName={adminName(admin)}
        title={actionIsRevoke ? "Revoke Council Access" : "Delete Admin"}
        confirmLabel={actionIsRevoke ? "Revoke Access" : "Delete Admin"}
        description={
          actionIsRevoke ? (
            <>
              Remove <strong>{adminName(admin)}</strong> from this council. Other council
              assignments remain active.
            </>
          ) : (
            <>
              Delete <strong>{adminName(admin)}</strong>. This action cannot be undone.
            </>
          )
        }
        onConfirm={deleteAdmin}
      />
    </div>
  )
}

function formatDateTime(value?: string) {
  if (!value) return "Unknown time"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date)
}

function normalizeRole(role?: string) {
  const value = role?.toLowerCase()
  return value === "super_admin" || value === "read_only" ? value : "admin"
}

function capitalizeLabel(value: string) {
  if (!value) return ""
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function roleBadgeLabel(role?: string) {
  const normalized = normalizeRole(role)
  if (normalized === "super_admin") return "N3 Admin"
  return normalized
    .split("_")
    .map(capitalizeLabel)
    .join(" ")
}

function rolePresetsFromDetail(
  role?: string,
  permissions: AdminPermissionDTO[] = [],
  councilId?: number,
): AdminRolePreset[] {
  if (normalizeRole(role) === "super_admin") return ["super_admin"]
  const hasWrite = permissions.some(
    (permission) =>
      permission.councilId === councilId &&
      (permission.action === "write_own" || permission.action === "write_all"),
  )
  return hasWrite ? ["admin"] : ["read_only"]
}
